import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { analyzeTrademark, calculatePrice, fetchSessionData } from '../api';
import { trackStep } from '../utils/analytics';
import {
  STEPS,
  stepToNumber,
  stepToTitle,
  JURISDICTION_CHOICES,
  FINAL_STEP_TYPES,
  APPLICANT_COUNT,
  COLOR_CHOICE,
  SERVICE_DETAILS
} from '../constants';

// State machine for transitions
const transitions = {
  [STEPS.JURISDICTION]: {
    next: (data) => (data.choice === JURISDICTION_CHOICES.UA ? STEPS.DETAILS_UA : STEPS.CONSULTATION_INTL),
  },
  [STEPS.DETAILS_UA]: {
    next: (data) => (data.classIds && data.classIds.length > 0 ? STEPS.PACKAGES : STEPS.STRATEGY),
    back: STEPS.JURISDICTION,
  },
  [STEPS.CONSULTATION_INTL]: {
    next: STEPS.SUCCESS, // Or a specific consultation success page
    back: STEPS.JURISDICTION,
  },
  [STEPS.STRATEGY]: {
    next: STEPS.PACKAGES,
    back: STEPS.DETAILS_UA,
  },
  [STEPS.PACKAGES]: {
    next: STEPS.LEGAL_CHECK,
    back: (data) => (data.classIds && data.classIds.length > 0 && (data.source === 'search' || data.source === 'analysis') ? STEPS.DETAILS_UA : STEPS.STRATEGY),
  },
  [STEPS.LEGAL_CHECK]: {
    next: STEPS.CALCULATION,
    back: STEPS.PACKAGES,
  },
  [STEPS.CALCULATION]: {
    next: STEPS.CHECKOUT,
    back: STEPS.LEGAL_CHECK,
  },
  [STEPS.CHECKOUT]: {
    back: STEPS.CALCULATION,
  },
};

export const useQuiz = () => {
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(STEPS.JURISDICTION);
  const [quizData, setQuizData] = useState({ needsSearch: true });
  const [direction, setDirection] = useState('forward');
  const [calculationResult, setCalculationResult] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Rehydrate state from Search or Analysis URL parameters
  useEffect(() => {
    const analysisId = searchParams.get('analysis_id');
    const searchId = searchParams.get('search_id');
    const source = searchParams.get('source');
    const paymentStatus = searchParams.get('paymentStatus');
    const id = analysisId || searchId;

    const directTm = searchParams.get('tm') || searchParams.get('trademarkName');
    const directClasses = searchParams.get('classes') || searchParams.get('classIds');
    const directDesc = searchParams.get('desc') || searchParams.get('activityDescription');

    if (id && source) {
      setIsLoading(true);
      fetchSessionData(id, source)
        .then((data) => {
          if (data && data.success) {
            const classIds = Array.isArray(data.classIds) ? data.classIds : [];

            setQuizData((prev) => ({
              ...prev,
              choice: JURISDICTION_CHOICES.UA,
              trademarkName: data.trademarkName || prev.trademarkName || '',
              activityDescription: data.activityDescription || prev.activityDescription || '',
              classIds: classIds.length > 0 ? classIds : prev.classIds || [],
              classCount: classIds.length || prev.classCount || 0,
              registrationType: prev.registrationType || 'name',
              source,
              search_id: data.searchId || searchId || null,
              analysis_id: data.analysisId || analysisId || null,
              paymentStatus: data.paymentStatus || paymentStatus || null,
              analysisStatus: data.analysisStatus || null,
            }));

            // Direct user straight to Ukraine TM details step
            setCurrentStep(STEPS.DETAILS_UA);
          }
        })
        .catch((err) => {
          console.warn('Could not rehydrate quiz session from API:', err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else if (directTm || directClasses) {
      const parsedClasses = directClasses
        ? directClasses.split(',').map((c) => parseInt(c.trim(), 10)).filter(Boolean)
        : [];

      setQuizData((prev) => ({
        ...prev,
        choice: JURISDICTION_CHOICES.UA,
        trademarkName: directTm || prev.trademarkName || '',
        activityDescription: directDesc || prev.activityDescription || '',
        classIds: parsedClasses.length > 0 ? parsedClasses : prev.classIds || [],
        classCount: parsedClasses.length || prev.classCount || 0,
        registrationType: prev.registrationType || 'name',
        source: source || 'direct',
      }));

      setCurrentStep(STEPS.DETAILS_UA);
    }
  }, [searchParams]);

  // Tracking step changes
  useEffect(() => {
    trackStep(currentStep, stepToNumber[currentStep]);
  }, [currentStep]);

  const handleNextStep = useCallback(async (data) => {
    setDirection('forward');
    const newQuizData = { ...quizData, ...data };
    setQuizData(newQuizData);
    setError(null);

    if (data.finalStep === FINAL_STEP_TYPES.CONSULTATION) {
      navigate(STEPS.SUCCESS);
      return;
    }

    const nextStep = transitions[currentStep]?.next;
    if (nextStep) {
      const nextStepValue = typeof nextStep === 'function' ? nextStep(newQuizData) : nextStep;
      setCurrentStep(nextStepValue);

      // Handle async operations based on the step we are *going to*
      if (nextStepValue === STEPS.STRATEGY) {
        setIsLoading(true);
        try {
          const analysisData = await analyzeTrademark(newQuizData.activityDescription);
          if (analysisData.success) {
            setAnalysisResult(analysisData.strategies);
          } else {
            throw new Error(analysisData.message || 'Analysis failed');
          }
        } catch (err) {
          setError("Сталася помилка, спробуйте пізніше, ми вже знаємо про проблему та працюємо над нею");
        } finally {
          setIsLoading(false);
        }
      } else if (nextStepValue === STEPS.LEGAL_CHECK) {
        if (data.package_type) {
          const packageDetails = SERVICE_DETAILS[data.package_type];
          if (packageDetails) {
            const serviceInfo = {
              serviceId: packageDetails.id,
              serviceName: packageDetails.name,
            };
            setQuizData((prev) => ({ ...prev, ...data, ...serviceInfo }));
          }
        }
      } else if (nextStepValue === STEPS.CALCULATION) {
        const pkgType = newQuizData.package_type;
        const packageDetails = SERVICE_DETAILS[pkgType];
        const serviceInfo = packageDetails ? {
          serviceId: packageDetails.id,
          serviceName: packageDetails.name,
        } : {};
        const updatedData = { ...newQuizData, ...serviceInfo };
        setQuizData(updatedData);
        
        setIsLoading(true);
        try {
          const requestData = {
            classCount: updatedData.classCount,
            applierCount: updatedData.numApplicants === APPLICANT_COUNT.ONE ? 1 : 2,
            hasColor: updatedData.isColor === COLOR_CHOICE.YES,
            servicePackage: updatedData.package_type,
            needsSearch: updatedData.needsSearch !== undefined ? updatedData.needsSearch : true,
          };
          const calcResult = await calculatePrice(requestData);
          setCalculationResult(calcResult);
        } catch (err) {
          setError("Сталася помилка, спробуйте пізніше, ми вже знаємо про проблему та працюємо над нею");
        } finally {
          setIsLoading(false);
        }
      }
    }
  }, [quizData, currentStep, navigate]);

  const handleBack = useCallback(() => {
    setDirection('backward');
    const backStep = transitions[currentStep]?.back;
    if (backStep) {
      const backStepValue = typeof backStep === 'function' ? backStep(quizData) : backStep;
      setCurrentStep(backStepValue);
    }
  }, [currentStep, quizData]);

  return {
    currentStep,
    quizData,
    direction,
    calculationResult,
    analysisResult,
    isLoading,
    error,
    handleNextStep,
    handleBack,
    stepToNumber,
    stepToTitle,
  };
};
